import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LeilaoConfig, LeilaoFilters, LeilaoLead, LeilaoPageMeta, LeilaoPurchase } from '../../../models/leilao.model';
import {
	getLeilaoConfig,
	listLeilaoLeads,
	listLeilaoPurchases,
	purchaseLeilaoLeads,
	updateLeilaoConfig
} from '../../../services/api/leilao/leilao.datric.api';
import { toastService } from '../../../services/toast.service';
import { setBalance } from '../auth/auth.slice';

export interface LeilaoState {
	leads: LeilaoLead[];
	meta: LeilaoPageMeta | null;
	loading: boolean;
	purchasing: boolean;
	purchases: LeilaoPurchase[];
	purchasesMeta: LeilaoPageMeta | null;
	loadingPurchases: boolean;
	config: LeilaoConfig | null;
	// Última falha ao carregar o catálogo. Sem isso, erro de configuração ou de
	// integração chegava na tela como "nenhum lead encontrado" e ficava invisível.
	error: string | null;
}

const initialState: LeilaoState = {
	leads: [],
	meta: null,
	loading: false,
	purchasing: false,
	purchases: [],
	purchasesMeta: null,
	loadingPurchases: false,
	config: null,
	error: null
};

export const fetchLeilaoLeads = createAsyncThunk(
	'leilao/leads',
	async (filters: LeilaoFilters, { rejectWithValue }) => {
		try {
			return await listLeilaoLeads(filters);
		} catch (e: any) {
			// O DatricRequests já desembrulha `message` da API; o resto vira texto.
			return rejectWithValue(typeof e === 'string' ? e : e?.message || 'Falha ao carregar o catálogo do leilão');
		}
	}
);

export const fetchLeilaoPurchases = createAsyncThunk(
	'leilao/purchases',
	async (params: { page?: number; limit?: number }) => {
		return listLeilaoPurchases(params);
	}
);

// A compra devolve os leads já abertos e o saldo novo — atualizamos o contador
// do navbar na hora, sem esperar um refetch do saldo.
export const purchaseLeads = createAsyncThunk(
	'leilao/purchase',
	async (ids: string[], { dispatch, rejectWithValue }) => {
		try {
			const result = await purchaseLeilaoLeads(ids);
			dispatch(setBalance(result.balance));
			const partes = [`${result.leads.length} lead(s) liberado(s)`];
			if (result.charged > 0) partes.push(`${result.charged} crédito(s) debitado(s)`);
			if (result.free.length) partes.push(`${result.free.length} já no seu histórico`);
			if (result.skipped.length) partes.push(`${result.skipped.length} indisponível(is)`);
			toastService.success(partes.join(' · '));
			return result;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao comprar os leads');
			return rejectWithValue(e);
		}
	}
);

export const fetchLeilaoConfig = createAsyncThunk('leilao/config', async () => {
	return getLeilaoConfig();
});

export const saveLeilaoConfig = createAsyncThunk(
	'leilao/saveConfig',
	async (payload: Partial<LeilaoConfig>, { rejectWithValue }) => {
		try {
			const config = await updateLeilaoConfig(payload);
			toastService.success('Configuração do leilão salva');
			return config;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao salvar a configuração');
			return rejectWithValue(e);
		}
	}
);

export const leilaoSlice = createSlice({
	name: 'leilao',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(fetchLeilaoLeads.pending, state => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(fetchLeilaoLeads.fulfilled, (state, action) => {
			state.loading = false;
			state.error = null;
			state.leads = action.payload.data;
			state.meta = action.payload.meta;
		});
		builder.addCase(fetchLeilaoLeads.rejected, (state, action) => {
			state.loading = false;
			// Zera a lista junto com o erro: manter o resultado anterior ao lado da
			// mensagem de falha faria parecer que o filtro novo trouxe aqueles leads.
			state.leads = [];
			state.meta = null;
			state.error = (action.payload as string) || 'Falha ao carregar o catálogo do leilão';
		});

		builder.addCase(fetchLeilaoPurchases.pending, state => {
			state.loadingPurchases = true;
		});
		builder.addCase(fetchLeilaoPurchases.fulfilled, (state, action) => {
			state.loadingPurchases = false;
			state.purchases = action.payload.data;
			state.purchasesMeta = action.payload.meta;
		});
		builder.addCase(fetchLeilaoPurchases.rejected, state => {
			state.loadingPurchases = false;
		});

		builder.addCase(purchaseLeads.pending, state => {
			state.purchasing = true;
		});
		builder.addCase(purchaseLeads.rejected, state => {
			state.purchasing = false;
		});
		// Substitui as linhas compradas pelas versões abertas, no lugar: o cliente
		// vê o telefone aparecer sem a tela recarregar.
		builder.addCase(purchaseLeads.fulfilled, (state, action) => {
			state.purchasing = false;
			const byId = new Map(action.payload.leads.map(l => [l.id, l]));
			state.leads = state.leads.map(lead => {
				const bought = byId.get(lead.id);
				return bought ? { ...lead, ...bought, price: 0, owned: true } : lead;
			});
		});

		builder.addCase(fetchLeilaoConfig.fulfilled, (state, action) => {
			state.config = action.payload;
		});
		builder.addCase(saveLeilaoConfig.fulfilled, (state, action) => {
			state.config = action.payload;
		});
	}
});
